/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { action, computed, observable } from 'mobx';
import { PassedFilters } from 'src/luis/projects/analytics/types/PassedFilters';
import { ScoreFilter } from 'src/luis/projects/utterances/types/Filters';
import { ModelType } from 'src/luis/types/ModelType';
import { LuisId } from 'src/luis/types/Resource';
import { FiltersItem, FilterType, SelectionChange } from 'src/verticals/utterancesFilters/types/Filters';

/**
 * Inverts the filter item if it matches the target one.
 *
 * @param filterItem The filter item to be inverted.
 * @param targetItem The target filter item to match with.
 */
const invertFilterItem = (filterItem: FiltersItem, targetId: LuisId): FiltersItem => {
    if (filterItem.id === targetId) {
        return { ...filterItem, isChecked: !filterItem.isChecked };
    }

    return filterItem;
};

/**
 * Reactive store for the utterance filters.
 */
export class UtteranceFiltersStore {
    @observable
    public intentFilterItems: FiltersItem[];

    @observable
    public entityFilterItems: FiltersItem[];

    @observable
    public differenceFilterItems: FiltersItem[];

    @observable
    public scoreWindow: ScoreFilter;

    constructor() {
        this.intentFilterItems = [];
        this.entityFilterItems = [];
        this.differenceFilterItems = [];
        this.scoreWindow = { min: 0, max: 1 };
    }

    @computed
    public get appliedIntentsFilter(): FiltersItem[] {
        return this.intentFilterItems.filter(item => item.isChecked);
    }

    @computed
    public get appliedEntitiesFilter(): FiltersItem[] {
        return this.entityFilterItems.filter(item => item.isChecked);
    }

    @computed
    public get appliedDifferenceFilter(): FiltersItem[] {
        return this.differenceFilterItems.filter(item => item.isChecked);
    }

    @computed
    public get appliedFilters(): FiltersItem[] {
        return [...this.appliedIntentsFilter, ...this.appliedEntitiesFilter, ...this.appliedDifferenceFilter];
    }

    @computed
    public get withinScoreWindow(): boolean {
        return this.scoreWindow.min !== 0 || this.scoreWindow.max !== 1;
    }

    @computed
    public get areFilterItemsApplied(): boolean {
        return Boolean(this.appliedIntentsFilter.length || this.appliedEntitiesFilter.length || this.appliedDifferenceFilter.length);
    }

    @computed
    public get isFilterModeOn(): boolean {
        return Boolean(this.areFilterItemsApplied || this.withinScoreWindow);
    }

    @computed
    public get isIncorrectFilterActive(): boolean {
        return (
            this.differenceFilterItems.find(f => f.id === 'incorrect') &&
            this.differenceFilterItems.find(f => f.id === 'incorrect').isChecked
        );
    }

    @computed
    public get isUnclearFilterActive(): boolean {
        return (
            this.differenceFilterItems.find(f => f.id === 'incorrect') && this.differenceFilterItems.find(f => f.id === 'unclear').isChecked
        );
    }

    /**
     * Initializes the filters values with the given
     * intents.
     */
    @action
    public initializeIntentFilterValues(intents: { id: string | number; name: string }[], activeNearestRivalIds: (string | number)[] = []) {
        this.intentFilterItems = intents.map<FiltersItem>(intent => ({
            id: intent.id,
            name: intent.name,
            filterType: 'intents',
            isChecked: activeNearestRivalIds.some(id => id === intent.id)
        }));
    }

    /**
     * Initializes the filters values with the given
     * entities.
     */
    @action
    public initializeEntityFilterValues(
        entities: { id: string | number; name: string; typeId?: ModelType }[],
        selectedEntitiesIds: string[] = []
    ) {
        this.entityFilterItems = entities.map<FiltersItem>(entity => ({
            id: entity.id,
            name: entity.name,
            filterType: 'entities',
            entityType: entity.typeId,
            isChecked: selectedEntitiesIds.some(id => id === entity.id)
        }));
    }

    /**
     * Initializes the difference filter values with the optional initial values.
     */
    @action
    public initializeDifferenceFilterValues(activeDifferenceFiltersInit: ('correct' | 'incorrect' | 'unclear')[] = []) {
        this.differenceFilterItems = [
            {
                id: 'unclear',
                name: 'Unclear',
                isChecked: activeDifferenceFiltersInit.some(d => d === 'unclear'),
                filterType: 'difference'
            },
            {
                id: 'correct',
                name: 'Correct',
                isChecked: activeDifferenceFiltersInit.some(d => d === 'correct'),
                filterType: 'difference'
            },
            {
                id: 'incorrect',
                name: 'Incorrect',
                isChecked: activeDifferenceFiltersInit.some(d => d === 'incorrect'),
                filterType: 'difference'
            }
        ];
    }

    /**
     * Initializes the score filter's window.
     */
    @action
    public initializeScoreFilterValues(windowSize?: ScoreFilter) {
        this.scoreWindow = windowSize ?? { min: 0, max: 1 };
    }

    /**
     * Initializes the filters values with the given
     * intents and entities.
     */
    @action
    public initializeFiltersValues(
        intents: { id: string | number; name: string }[],
        entities: { id: string | number; name: string; typeId?: ModelType }[],
        activeDifferenceFiltersInit: ('correct' | 'incorrect' | 'unclear')[],
        activeNearestRivalIds: LuisId[]
    ) {
        this.intentFilterItems = intents.map<FiltersItem>(intent => ({
            id: intent.id,
            name: intent.name,
            filterType: 'intents',
            isChecked: activeNearestRivalIds.some(id => id === intent.id)
        }));

        this.entityFilterItems = entities.map<FiltersItem>(entity => ({
            id: entity.id,
            name: entity.name,
            filterType: 'entities',
            entityType: entity.typeId,
            isChecked: false
        }));

        this.differenceFilterItems = [
            {
                id: 'unclear',
                name: 'Unclear',
                isChecked: activeDifferenceFiltersInit.some(d => d === 'unclear'),
                filterType: 'difference'
            },
            {
                id: 'correct',
                name: 'Correct',
                isChecked: activeDifferenceFiltersInit.some(d => d === 'correct'),
                filterType: 'difference'
            },
            {
                id: 'incorrect',
                name: 'Incorrect',
                isChecked: activeDifferenceFiltersInit.some(d => d === 'incorrect'),
                filterType: 'difference'
            }
        ];

        this.scoreWindow = { min: 0, max: 1 };
    }

    /**
     * Updates the filter items after selecting/unselecting all.
     */
    @action
    public selectAll(filterType: FilterType, checked: boolean) {
        switch (filterType) {
            case 'entities':
                this.entityFilterItems = this.entityFilterItems.map(item => ({ ...item, isChecked: checked }));
                break;
            case 'intents':
                this.intentFilterItems = this.intentFilterItems.map(item => ({ ...item, isChecked: checked }));
                break;
            default:
        }
    }

    /**
     * Updates the change occurred in the filter items.
     */
    @action
    public changeFilter(change: SelectionChange) {
        switch (change.changeType) {
            case 'intents':
                this.intentFilterItems = this.intentFilterItems.map(filterItem => invertFilterItem(filterItem, change.args.id));
                break;
            case 'difference':
                this.differenceFilterItems = this.differenceFilterItems.map(filterItem => invertFilterItem(filterItem, change.args.id));
                break;
            case 'score':
                this.scoreWindow = { min: change.args.min, max: change.args.max };
                break;
            case 'entities':
            default:
                this.entityFilterItems = this.entityFilterItems.map(filterItem => invertFilterItem(filterItem, change.args.id));
                break;
        }
    }

    /**
     * Updates the difference filters by the given difference filter type.
     */
    @action
    public applyDifferenceFilters(filters: PassedFilters['differenceFilters']) {
        if (!filters.length) {
            return;
        }

        this.differenceFilterItems = this.differenceFilterItems.map(filterItem => ({
            ...filterItem,
            isChecked: filterItem.isChecked || filters.indexOf(<'correct' | 'incorrect' | 'unclear'>filterItem.id) !== -1
        }));
    }

    /**
     * Reset the filters selection state.
     */
    @action
    public clearFilters() {
        this.intentFilterItems = this.intentFilterItems.map(f => ({ ...f, isChecked: false }));
        this.entityFilterItems = this.entityFilterItems.map(f => ({ ...f, isChecked: false }));
        this.differenceFilterItems = this.differenceFilterItems.map(f => ({ ...f, isChecked: false }));
        this.scoreWindow = { min: 0, max: 1 };
    }
}
